import {AG_GRID_COMPATIBILITY, JustTooltipFormatter, ProgressBarFormatter, TableSorting, useFormatters} from '@wallaby-ui/document';
import {get} from 'lodash';
import {Column} from 'material-table';
import * as React from 'react';
import {useCallback, useMemo} from 'react';

import {DepartmentTableRowType, DepartmentTableRowTypeWithTotal, ReportType, reportTypeToTableOps} from './departmenttypes';

const EMPTY = '-';

export const useDepartmentReportTableColumns = (
  reportType: ReportType,
  categoryColumnTitle: string,
  getCategory: (data: DepartmentTableRowTypeWithTotal) => string,
  currentSorting: TableSorting
) => {
  const {formatFloat, t} = useFormatters();

  const createCategoryFormatter = useCallback(
    (
      prop: string,
      title: string,
      getCategory: (data: DepartmentTableRowTypeWithTotal) => string,
      width: string | undefined = undefined
    ) => {
      return {
        field: prop,
        title,
        render: (data: DepartmentTableRowTypeWithTotal) => {
          const propFormatted = getCategory(data) ?? EMPTY;
          return (
            <JustTooltipFormatter
              {...AG_GRID_COMPATIBILITY}
              value={propFormatted ?? EMPTY}
              tooltipOverride={propFormatted}
              bold={data.isTotalRow}
            />
          );
        },
        width,
        sorting: true,
      };
    },
    []
  );

  const createFloatFormatter = useCallback(
    (prop: string, dataProp: Exclude<keyof DepartmentTableRowType, 'category'>, title: string, width: string | undefined = undefined) => {
      return {
        field: prop,
        title,
        render: (data: DepartmentTableRowTypeWithTotal) => {
          const propFormatted = formatFloat(data[dataProp]) ?? EMPTY;
          return (
            <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={propFormatted} tooltipOverride={propFormatted} bold={data.isTotalRow} />
          );
        },
        width,
        sorting: true,
      };
    },
    [formatFloat]
  );

  const createPercentageFormatter = useCallback(
    (prop: string, dataProp: Exclude<keyof DepartmentTableRowType, 'category'>, title: string, width: string | undefined = undefined) => {
      return {
        field: prop,
        title,
        render: (data: DepartmentTableRowTypeWithTotal) => {
          const percent = data[dataProp];
          const percentString = `${formatFloat(percent)} %` ?? EMPTY;
          return (
            <ProgressBarFormatter
              {...AG_GRID_COMPATIBILITY}
              value={percentString}
              tooltipOverride={percentString}
              progressPercent={percent}
              bold={data.isTotalRow}
              style={{maxWidth: '150px'}}
            />
          );
        },
        width,
        sorting: true,
      };
    },
    [formatFloat]
  );

  return useMemo(() => {
    const columns: Column<DepartmentTableRowTypeWithTotal>[] = [
      createCategoryFormatter(`category`, categoryColumnTitle, getCategory),
      createFloatFormatter(`measureCount`, 'measureCount', t('reports.totalRow')),
      createPercentageFormatter(
        `measureCountToHospCountPct`,
        'measureCountToHospCountPct',
        t('department.report.charttable.column.numberOfAdmissions')
      ),
      createFloatFormatter(`measureCountPer1000HospDays`, 'measureCountPer1000HospDays', t('hospital.network.report.charts.caredays.name')),
    ];
    return columns.map((column) => ({
      ...column,
      sorting: column.sorting ?? false,
      customSort: (row1, row2, _type) => {
        const isCurrentColumn = column.field === currentSorting.field;
        const isAscending = isCurrentColumn ? currentSorting.direction === 'asc' : true;

        if (row1?.isTotalRow) return isAscending ? -1 : 1;
        if (row2?.isTotalRow) return isAscending ? 1 : -1;

        const field = column.field ?? '';
        const v1 = field === 'category' ? getCategory(row1) : get(row1, field);
        const v2 = field === 'category' ? getCategory(row2) : get(row2, field);

        const primary = compare(v1, v2);
        if (primary !== 0) return primary;

        const firstField = columns?.[0]?.field as string | undefined;
        if (firstField && firstField !== field) {
          const s1 = firstField === 'category' ? getCategory(row1) : get(row1, firstField);
          const s2 = firstField === 'category' ? getCategory(row2) : get(row2, firstField);
          const secondary = compare(s1, s2);
          if (secondary !== 0) {
            return isAscending ? secondary : -secondary;
          }
        }

        return 0;
      },
      ...(column.field === currentSorting.field ? {defaultSort: currentSorting.direction ?? undefined} : {}),
    }));
  }, [
    categoryColumnTitle,
    createCategoryFormatter,
    createFloatFormatter,
    createPercentageFormatter,
    currentSorting.direction,
    currentSorting.field,
    getCategory,
    t,
  ]);
};

const compare = (a: any, b: any) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, {numeric: true, sensitivity: 'base'});
};
